// Stripe Express status card — Feb 19, 2026.
//
// Lives on the SuperAdmin Overview tab + can be reused elsewhere. The one
// thing that gates EVERY fan payment on the platform is whether the silo
// owner has finished Stripe Express onboarding. If a creator's Stripe
// charges_enabled is false, fans see "this creator hasn't finished Stripe
// setup yet" and can't subscribe.
//
// This card surfaces:
//   • Current state (Not connected / Onboarding pending / Ready)
//   • A primary CTA that opens the right Stripe-hosted flow (Account Link
//     for new/incomplete accounts, Express Login Link for finished ones)
//   • A muted "Connected as acct_xxx" line so admins can verify which
//     Stripe account is wired up
import { useEffect, useState } from "react";
import { CreditCard, ArrowSquareOut, CheckCircle, Clock, Warning, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function StripeStatusCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  // Feb 21, 2026 (late eve) — pre-fetch the Express dashboard URL when
  // the user is in "ready" state, so the CTA can be a real <a target="_blank">
  // instead of a JS-driven popup (which iOS Safari was silently blocking
  // and falling through to a same-tab redirect → Randy's "white page").
  const [dashUrl, setDashUrl] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/stripe/config");
      setCfg(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Only fetch if Stripe is ready — otherwise the dashboard endpoint 400s.
    if (!cfg?.charges_enabled || !cfg?.payouts_enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/stripe/connect/dashboard");
        if (!cancelled && r.data?.url) setDashUrl(r.data.url);
      } catch { /* silent — button will fall back to fetch-on-click */ }
    })();
    return () => { cancelled = true; };
  }, [cfg?.charges_enabled, cfg?.payouts_enabled]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/start", { origin_url: window.location.origin });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No URL returned");
      }
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Couldn't open Stripe.");
    }
  };

  // Feb 24, 2026 (Randy pt-founder-stripe-connect) — disconnect the
  // current Stripe account (including simulated ones planted by
  // /admin/simulate-payouts). Reverts the card to `state=none` so
  // the user can start a fresh Connect flow. Confirm-gated so an
  // accidental click doesn't nuke a real bound account.
  const disconnect = async () => {
    if (!window.confirm(
      "Disconnect this Stripe account?\n\n" +
      "This severs the local pointer only — your Stripe account on Stripe's " +
      "side is untouched. You can start a fresh Connect Express onboarding " +
      "flow right after. If this was a simulated / test-mode account, this " +
      "clears it so you can wire up a real one."
    )) return;
    setBusy(true);
    try {
      await api.post("/stripe/connect/disconnect");
      setDashUrl(null);
      await load();
      toast.success("Disconnected. Ready to connect a fresh account.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed.");
    } finally {
      setBusy(false);
    }
  };

  // Feb 24, 2026 — fallback for when the pre-fetch of dashUrl fails
  // (typically because the account is simulated / not real on Stripe's
  // side). Instead of a permanently stuck "Preparing link…" button,
  // this handler hits the endpoint on-click and surfaces the error as
  // a toast if it fails.
  const openDashboardOnClick = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/dashboard");
      if (r.data?.url) {
        window.open(r.data.url, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("No dashboard URL");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open Stripe dashboard. If this is a simulated account, disconnect it first.");
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return (
      <div className="bg-white border hairline rounded-xl p-5 flex items-center gap-2 text-xs text-[#6B6A68]" data-testid="stripe-status-card-loading">
        <Spinner size={14} className="animate-spin" /> Loading Stripe status…
      </div>
    );
  }

  const charges = !!cfg.charges_enabled;
  const payouts = !!cfg.payouts_enabled;
  const hasAccount = !!cfg.stripe_account_id;
  const state = charges && payouts ? "ready"
              : hasAccount ? "pending"
              : "none";

  const tone = state === "ready"
    ? { bg: "bg-[#E8F0E9]", border: "border-[#9DBCA1]", text: "text-[#2F6B3A]", Icon: CheckCircle, label: "Stripe ready" }
    : state === "pending"
      ? { bg: "bg-[#F8F1E9]", border: "border-[#E5C77A]", text: "text-[#8A5A00]", Icon: Clock, label: "Onboarding pending" }
      : { bg: "bg-[#FDF1ED]", border: "border-[#E5BBA8]", text: "text-[#7A3A2A]", Icon: Warning, label: "Not connected" };

  return (
    // Iter160w (Randy pt-siloart-seagreen-encased-sweep, Feb 2026) — the
    // sea-green ready state (#E8F0E9) + pending/none tones aren't in the
    // CSS whitetext/blacktext exclusion chain. Marking the outer card
    // `.siloart-encased` explicitly so all inner text keeps its native
    // color under Silo Art V2, matching AffiliateUpgrade + SiloLLMHeader.
    <div className={`border-2 ${tone.border} ${tone.bg} siloart-encased rounded-xl p-5`} data-testid="stripe-status-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-white border hairline flex items-center justify-center shrink-0">
          <CreditCard size={20} weight="duotone" className="text-[#4A5D4E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-serif-display text-lg text-[#1C1B1A]">Stripe Express · your bank-of-record</h3>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded ${tone.text} bg-white border hairline`} data-testid="stripe-status-pill">
              <tone.Icon size={11} weight="fill" /> {tone.label}
            </span>
          </div>
          <p className="text-xs text-[#6B6A68] mb-3 max-w-2xl">
            {state === "ready" && (
              <>You're all set. Fans can subscribe to your silos and you can withdraw to your bank. Test-mode payouts settle instantly.</>
            )}
            {state === "pending" && (
              <>You started Stripe Express onboarding but haven't finished it. Until you do, <strong>no fan can pay for a subscription on your silos</strong>. Use any Stripe test routing/account number (e.g. 110000000 / 000123456789) to finish in test mode.</>
            )}
            {state === "none" && (
              <>Connect Stripe Express so fans can subscribe to your silos. This is what's blocking RJ (and any other fan) from paying for Cosmic Revelations. You can use Stripe's test mode forever — no real money moves until you switch to live keys.</>
            )}
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            {state === "ready" ? (
              <>
                {dashUrl ? (
                  <a
                    href={dashUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stripe-status-card-dashboard-btn"
                    className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5"
                  >
                    Open Stripe dashboard <ArrowSquareOut size={12} />
                  </a>
                ) : (
                  /* Feb 24, 2026 (Randy pt-founder-stripe-connect) —
                     fetch-on-click fallback replaces the previously-
                     stuck "Preparing link…" spinner. If the pre-fetch
                     failed (usually because the bound account is
                     simulated / not real on Stripe's side), this
                     surfaces the error as a toast instead of a dead
                     button. */
                  <button
                    type="button"
                    onClick={openDashboardOnClick}
                    disabled={busy}
                    data-testid="stripe-status-card-dashboard-btn"
                    className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {busy ? (<><Spinner size={12} className="animate-spin" /> Opening…</>) : (<>Open Stripe dashboard <ArrowSquareOut size={12} /></>)}
                  </button>
                )}
                {/* Feb 24, 2026 (Randy pt-founder-stripe-connect) —
                    disconnect button lives in the READY state so an
                    admin (Randy) can clear a simulated account and
                    complete real Connect Express onboarding at will. */}
                <button
                  type="button"
                  onClick={disconnect}
                  disabled={busy}
                  data-testid="stripe-status-card-disconnect-btn"
                  className="text-xs px-3 py-2 rounded-md bg-transparent border border-[#C16E5A] text-[#7A3A2A] hover:bg-[#FDF1ED] inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  Disconnect this account
                </button>
              </>
            ) : (
              <button
                onClick={startOnboarding}
                disabled={busy}
                data-testid="stripe-status-card-onboard-btn"
                className="text-xs px-3 py-2 rounded-md bg-[#C16E5A] text-[#FDFCF8] hover:bg-[#A85540] flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy ? "Opening Stripe…" : state === "pending" ? "Finish onboarding" : "Connect Stripe Express"} <ArrowSquareOut size={12} />
              </button>
            )}
            {hasAccount && (
              <span className="text-[10px] text-[#6B6A68] font-mono select-all" data-testid="stripe-status-card-account-id">
                Connected as {cfg.stripe_account_id}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
